import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import LookupSelect from "@/components/images/LookupSelect";
import type { DealProductItem, ProductType, MeasurementFieldConfig } from "@shared/api";
import { getMeasurementFieldLabel } from "@shared/api";

interface ProductItemFormProps {
  productType: ProductType;
  item: DealProductItem;
  itemLabel: string; // e.g. "Garázskapu 1"
  onChange: (item: DealProductItem) => void;
}

export function ProductItemForm({ productType, item, itemLabel, onChange }: ProductItemFormProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language === "sk" ? "sk" : i18n.language === "en" ? "en" : "hu") as "hu" | "sk" | "en";
  const fields = productType.measurementFields || productType.measurement_fields || [];
  const fieldLabel = (f: MeasurementFieldConfig) => getMeasurementFieldLabel(f, lang);
  const [widths, setWidths] = useState<string[]>(
    (item.widths && item.widths.length >= 2) ? item.widths : ["", ""]
  );
  const [heights, setHeights] = useState<string[]>(
    (item.heights && item.heights.length >= 2) ? item.heights : ["", ""]
  );

  useEffect(() => {
    setWidths((item.widths && item.widths.length >= 2) ? item.widths : ["", ""]);
    setHeights((item.heights && item.heights.length >= 2) ? item.heights : ["", ""]);
  }, [item.id]);

  const updateItem = (updates: Partial<DealProductItem>) => {
    onChange({ ...item, ...updates });
  };

  const handleWidthChange = (index: number, value: string) => {
    const newWidths = [...widths];
    newWidths[index] = value;
    setWidths(newWidths);
    // Preserve full array so second (and further) values are never lost when saving
    updateItem({ widths: newWidths });
  };

  const handleHeightChange = (index: number, value: string) => {
    const newHeights = [...heights];
    newHeights[index] = value;
    setHeights(newHeights);
    // Preserve full array so second (and further) values are never lost when saving
    updateItem({ heights: newHeights });
  };

  return (
    <div className="space-y-6 p-4">
      <h3 className="text-lg font-semibold">{itemLabel}</h3>

      {fields.map((field) => {
        if (field.type === "dimensions") {
          if (field.key === "widths") {
            return (
              <div key={field.key} className="space-y-3">
                <label className="text-sm font-medium">
                  {fieldLabel(field)} {field.required && <span className="text-destructive">*</span>}
                </label>
                {widths.map((width, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder={`${fieldLabel(field)} ${index + 1}`}
                      value={width}
                      onChange={(e) => handleWidthChange(index, e.target.value)}
                      className="flex-1"
                    />
                    {widths.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const next = widths.filter((_, i) => i !== index);
                          setWidths(next.length >= 2 ? next : ["", ""]);
                          updateItem({ widths: next.length >= 2 ? next : ["", ""] });
                        }}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setWidths([...widths, ""])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("measurementExtra.addAnotherDimension")}
                </Button>
              </div>
            );
          }
          if (field.key === "heights") {
            return (
              <div key={field.key} className="space-y-3">
                <label className="text-sm font-medium">
                  {fieldLabel(field)} {field.required && <span className="text-destructive">*</span>}
                </label>
                {heights.map((height, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder={`${fieldLabel(field)} ${index + 1}`}
                      value={height}
                      onChange={(e) => handleHeightChange(index, e.target.value)}
                      className="flex-1"
                    />
                    {heights.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const next = heights.filter((_, i) => i !== index);
                          setHeights(next.length >= 2 ? next : ["", ""]);
                          updateItem({ heights: next.length >= 2 ? next : ["", ""] });
                        }}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setHeights([...heights, ""])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("measurementExtra.addAnotherDimension")}
                </Button>
              </div>
            );
          }
        }

        if (field.key === "surface" || field.key === "color" || field.key === "prelis") {
          const lookupType =
            field.key === "surface"
              ? "panel_structure"
              : field.key === "color"
                ? "color"
                : "prelis";
          const value =
            field.key === "surface"
              ? item.surface
              : field.key === "color"
                ? item.color
                : item.prelis;
          return (
            <LookupSelect
              key={field.key}
              type={lookupType}
              value={value}
              onValueChange={(value) =>
                updateItem(
                  field.key === "surface"
                    ? { surface: value || "" }
                    : field.key === "color"
                      ? { color: value }
                      : { prelis: value }
                )
              }
              label={fieldLabel(field)}
              placeholder={t("lookupSelect.chooseFieldPlaceholder", { field: fieldLabel(field) })}
            />
          );
        }

        if (field.type === "boolean") {
          const raw = ((item as unknown) as Record<string, unknown>)[field.key];
          const selectValue =
            raw === true || raw === "true" ? "true" : raw === false || raw === "false" ? "false" : "";
          return (
            <div key={field.key} className="space-y-2">
              <label className="text-sm font-medium">
                {fieldLabel(field)} {field.required && <span className="text-destructive">*</span>}
              </label>
              <Select
                value={selectValue || undefined}
                onValueChange={(v) =>
                  updateItem({ [field.key]: v === "true" } as Partial<DealProductItem>)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={fieldLabel(field)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t("common.yes")}</SelectItem>
                  <SelectItem value="false">{t("common.no")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          );
        }

        if (field.type === "textarea" || field.key === "notes") {
          const val = item.notes ?? "";
          return (
            <div key={field.key} className="space-y-2">
              <label className="text-sm font-medium">
                {fieldLabel(field)} {field.required && <span className="text-destructive">*</span>}
              </label>
              <Textarea
                placeholder={fieldLabel(field)}
                value={val}
                onChange={(e) => updateItem({ notes: e.target.value })}
                rows={4}
                className="resize-none"
              />
            </div>
          );
        }

        if (field.key === "type" || field.key === "power") {
          const val = field.key === "type" ? item.type ?? "" : item.power ?? "";
          return (
            <div key={field.key} className="space-y-2">
              <label className="text-sm font-medium">
                {fieldLabel(field)} {field.required && <span className="text-destructive">*</span>}
              </label>
              <Input
                value={val}
                onChange={(e) =>
                  updateItem(field.key === "type" ? { type: e.target.value } : { power: e.target.value })
                }
                placeholder={fieldLabel(field)}
              />
            </div>
          );
        }

        if (field.key === "depth" || (field.type === "number" && field.key !== "widths" && field.key !== "heights")) {
          const val = field.key === "depth"
            ? (item.depth ?? "")
            : ((item as unknown) as Record<string, string | undefined>)[field.key] ?? "";
          return (
            <div key={field.key} className="space-y-2">
              <label className="text-sm font-medium">
                {fieldLabel(field)} {field.required && <span className="text-destructive">*</span>}
              </label>
              <Input
                type="number"
                inputMode="decimal"
                value={val}
                onChange={(e) =>
                  updateItem(
                    field.key === "depth"
                      ? { depth: e.target.value }
                      : ({ [field.key]: e.target.value } as Partial<DealProductItem>)
                  )
                }
                placeholder={fieldLabel(field)}
              />
            </div>
          );
        }

        return (
          <div key={field.key} className="space-y-2">
            <label className="text-sm font-medium">
              {fieldLabel(field)} {field.required && <span className="text-destructive">*</span>}
            </label>
            <Input
              value={((item as unknown) as Record<string, string | undefined>)[field.key] ?? ""}
              onChange={(e) => updateItem({ [field.key]: e.target.value } as Partial<DealProductItem>)}
              placeholder={fieldLabel(field)}
            />
          </div>
        );
      })}
    </div>
  );
}
